import { EvmToken, FiatToken, getOnChainTokenDetails, Networks, OnChainTokenSymbol, RampDirection } from "@vortexfi/shared";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { getRampDirectionFromPath } from "../../helpers/path";
import { getLanguageFromPath, Language } from "../../translations/helpers";
import { useRampDirection } from "../rampDirectionStore";

export const DEFAULT_FIAT_TOKEN = FiatToken.EURC;
export const DEFAULT_PT_BR_TOKEN = FiatToken.BRL;

export const DEFAULT_BRL_AMOUNT = "100";
export const DEFAULT_EURC_AMOUNT = "30";
export const DEFAULT_ARS_AMOUNT = "20";
export const DEFAULT_MXN_AMOUNT = "100";
export const DEFAULT_COP_AMOUNT = "100";
export const DEFAULT_USD_AMOUNT = "100";

export const defaultFiatTokenAmounts: Record<FiatToken, string> = {
  [FiatToken.EURC]: DEFAULT_EURC_AMOUNT,
  [FiatToken.ARS]: DEFAULT_ARS_AMOUNT,
  [FiatToken.BRL]: DEFAULT_BRL_AMOUNT,
  [FiatToken.USD]: DEFAULT_USD_AMOUNT,
  [FiatToken.MXN]: DEFAULT_MXN_AMOUNT,
  [FiatToken.COP]: DEFAULT_COP_AMOUNT
};

const defaultFiatToken = getLanguageFromPath() === Language.Portuguese_Brazil ? DEFAULT_PT_BR_TOKEN : DEFAULT_FIAT_TOKEN;

const defaultFiatAmount =
  getLanguageFromPath() === Language.Portuguese_Brazil ? DEFAULT_BRL_AMOUNT : defaultFiatTokenAmounts[defaultFiatToken];

const defaultOnChainToken = EvmToken.USDC;

interface RampFormState {
  inputAmount: string;
  onChainToken: OnChainTokenSymbol;
  fiatToken: FiatToken;
  lastConstraintDirection: RampDirection;
  taxId?: string;
  pixId?: string;
}

interface RampFormActions {
  actions: {
    setInputAmount: (amount?: string) => void;
    setOnChainToken: (token: OnChainTokenSymbol) => void;
    setFiatToken: (token: FiatToken) => void;
    setConstraintDirection: (direction: RampDirection) => void;
    handleNetworkChange: (network: Networks) => void;
    reset: () => void;
    setTaxId: (taxId: string) => void;
    setPixId: (pixId: string) => void;
  };
}

export const DEFAULT_QUOTE_FORM_STORE_VALUES: RampFormState = {
  fiatToken: defaultFiatToken,
  inputAmount: defaultFiatAmount,
  lastConstraintDirection: getRampDirectionFromPath(),
  onChainToken: defaultOnChainToken,
  pixId: "",
  taxId: ""
};

export const useQuoteFormStore = create<RampFormState & RampFormActions>()(
  persist(
    (set, get) => ({
      ...DEFAULT_QUOTE_FORM_STORE_VALUES,
      actions: {
        handleNetworkChange: (network: Networks) => {
          const { onChainToken } = get();
          const onChainTokenDetails = getOnChainTokenDetails(network, onChainToken);
          if (!onChainTokenDetails) {
            // USDC is supported on all networks
            set({ onChainToken: EvmToken.USDC });
          }
        },

        reset: () => {
          set({
            ...DEFAULT_QUOTE_FORM_STORE_VALUES
          });
        },
        setConstraintDirection: (direction: RampDirection) => set({ lastConstraintDirection: direction }),
        setFiatToken: (token: FiatToken) => set({ fiatToken: token }),
        setInputAmount: (amount?: string) => set({ inputAmount: amount }),
        setOnChainToken: (token: OnChainTokenSymbol) => set({ onChainToken: token }),
        setPixId: (pixId: string) => set({ pixId }),
        setTaxId: (taxId: string) => set({ taxId })
      }
    }),
    {
      migrate: (persistedState, version) => {
        if (version !== 3) {
          return null;
        }
        return persistedState;
      },
      name: "useRampFormStore",
      partialize: state => ({
        fiatToken: state.fiatToken,
        inputAmount: state.inputAmount,
        lastConstraintDirection: state.lastConstraintDirection,
        onChainToken: state.onChainToken,
        pixId: state.pixId,
        taxId: state.taxId
      }),
      version: 3
    }
  )
);

export const useInputAmount = () => useQuoteFormStore(state => state.inputAmount);
export const useOnChainToken = () => useQuoteFormStore(state => state.onChainToken);
export const useFiatToken = () => useQuoteFormStore(state => state.fiatToken);
export const useLastConstraintDirection = () => useQuoteFormStore(state => state.lastConstraintDirection);
export const useTaxId = () => useQuoteFormStore(state => state.taxId);
export const usePixId = () => useQuoteFormStore(state => state.pixId);

export const useQuoteConstraintsValid = () => {
  const direction = useRampDirection();
  const lastConstraintDirection = useLastConstraintDirection();
  return direction === lastConstraintDirection;
};

export const useQuoteFormStoreActions = () => useQuoteFormStore(state => state.actions);
